import React, { useMemo } from "react";
import * as Actions from "actions";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import { $Switch } from "us.common/components";
import { RootState } from "us.helper/types";
import {
  getLockAllStatus,
  lockAllSection,
} from "components/TemplateEditor/Functions";
import { DEFAULT_PAGE_MARGINS } from "constants/TemplateEditor";
import { UpdateMessageTemplates } from "components/EditorHome/Repository";
import { getMessageTemplateAvailability } from "functions";

type CommonPropertiesProps = {
  isXml?: boolean;
};

const CommonProperties: React.FC<CommonPropertiesProps & PropsFromRedux> = (
  props
) => {
  const {
    isXml,
    template,
    messageTemplatesUpdate,
    updateTemplate,
    updateMessageTemplates,
  } = props;
  const { t } = useTranslation();

  const { templateContent, isMessageTemplateAdded, groupName, categoryName } =
    template.data ?? {};
  const isLockedAllSections = useMemo(() => {
    return getLockAllStatus(templateContent);
  }, [template.data?.templateContent]);

  /**
   * @description - Handle set page margins
   * @param isLocked - Lock status
   */
  const handleSetPageMargins = (isMarginApplied: boolean) => {
    const updatedTemplate = {
      ...template.data,
      templateContent: {
        ...templateContent,
        isMarginApplied: isMarginApplied,
        margin: { ...DEFAULT_PAGE_MARGINS },
      },
    };
    updateTemplate && updateTemplate(updatedTemplate);
  };

  /**
   * @description - Handle all section lock functionality
   * @param isLocked - Lock status
   */
  const handleLockAllSections = (isLocked: boolean) => {
    lockAllSection(isLocked);

    // update template
    const pages = templateContent.pages.map((page) => {
      return {
        ...page,
        sections: page.sections.map((section) => {
          return { ...section, locked: isLocked };
        }),
      };
    });

    const updatedTemplate = {
      ...template.data,
      templateContent: {
        ...templateContent,
        pages,
      },
    };
    updateTemplate && updateTemplate(updatedTemplate);
  };

  /**
   * @description - Handle add/remove template from message templates
   * @param isAdded - Added or not status
   */
  const handleAddToMessageTemplates = (isAdded: boolean) => {
    const params = UpdateMessageTemplates.call({
      templateId: template.data.templateId,
      isDelete: !isAdded,
    });
    // pass refreshAll boolean value to identify update all templates list or individual template details
    updateMessageTemplates &&
      updateMessageTemplates({ payload: params, refreshAll: false });
  };

  return (
    <>
      <div className="components-content p-2 pl-2">
        {!isXml && (
          <div className="d-flex justify-content-between mb-2">
            <label className="mr-2">
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ALIGNMENT_TOOL")}
            </label>
            <div>
              <$Switch
                name="pageMargins"
                size="small"
                checked={templateContent?.isMarginApplied}
                onChange={(isChecked: boolean) =>
                  handleSetPageMargins(isChecked)
                }
                disabled={!template?.data}
              />
            </div>
          </div>
        )}
        <div className="d-flex justify-content-between mb-2">
          <label className="mr-2">
            {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.LOCK_ALL_SECTIONS")}
          </label>
          <div>
            <$Switch
              size="small"
              name="lockAllSection"
              checked={isLockedAllSections}
              onChange={(isChecked: boolean) =>
                handleLockAllSections(isChecked)
              }
              disabled={!template?.data}
            />
          </div>
        </div>
        {getMessageTemplateAvailability(groupName, categoryName) && (
          <div className="d-flex justify-content-between mb-2">
            <label className="mr-2">
              {t(
                "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADD_TO_MESSAGE_TEMPLATES"
              )}
            </label>
            <div>
              <$Switch
                size="small"
                name="isMessageTemplateAdded"
                checked={isMessageTemplateAdded}
                onChange={(isChecked: boolean) =>
                  handleAddToMessageTemplates(isChecked)
                }
                disabled={!template?.data || messageTemplatesUpdate.isLoading}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templatesHistory, editorHome } = state;
  const { template } = templatesHistory;

  return {
    template,
    messageTemplatesUpdate: editorHome.messageTemplatesUpdate,
  };
};

const { template } = Actions.templatesHistory;
const { messageTemplates } = Actions.editorHome;

const mapDispatchToProps = {
  updateTemplate: template.update,
  updateMessageTemplates: messageTemplates.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CommonProperties);
